import { useState, useRef, useEffect, useMemo } from "react";
import { GitBranch, ChevronDown, Check, Search } from "lucide-react";

interface BranchSelectorProps {
  branches: { id: string; name: string }[];
  selectedId: string;
  onChange: (branchId: string) => void;
  label?: string;
  placeholder?: string;
}

export default function BranchSelector({
  branches,
  selectedId,
  onChange,
  label,
  placeholder = "Select branch…",
}: BranchSelectorProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const selected = branches.find((b) => b.id === selectedId);

  const filtered = useMemo(() => {
    if (!search.trim()) return branches;
    const q = search.toLowerCase();
    return branches.filter(b => b.name.toLowerCase().includes(q));
  }, [branches, search]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    if (open) { setSearch(""); setTimeout(() => searchRef.current?.focus(), 0); }
  }, [open]);

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">
          {label}
        </label>
      )}
      <div className="relative" ref={ref}>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="w-full flex items-center gap-2 pl-3 pr-2.5 py-2.5 rounded-lg border border-slate-200/80 bg-white text-[13px] text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-300 transition-colors"
        >
          <GitBranch className="size-4 text-indigo-400 shrink-0" />
          <span className={`flex-1 text-left truncate ${selected ? "text-slate-700 font-medium" : "text-slate-400"}`}>
            {selected ? selected.name : placeholder}
          </span>
          <ChevronDown className={`size-3.5 text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {open && (
          <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-hidden">
            {branches.length > 4 && (
              <div className="relative px-2 pt-2">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 mt-1 size-3.5 text-slate-400" />
                <input ref={searchRef} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filter branches…"
                  className="w-full pl-7 pr-2 py-1.5 text-[12px] rounded-md border border-slate-200 focus:outline-none focus:ring-1 focus:ring-indigo-300" />
              </div>
            )}
            <div className="max-h-48 overflow-y-auto p-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-center text-[11px] text-slate-400">{search ? "No matches" : "No branches"}</div>
            ) : (
              filtered.map((b) => {
                const isActive = b.id === selectedId;
                const isMain = b.name === "main";
                return (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => { onChange(b.id); setOpen(false); }}
                    className={`w-full flex items-center gap-2 px-3 py-2.5 text-[13px] rounded-md transition-colors ${
                      isActive
                        ? "bg-indigo-50 text-indigo-700"
                        : "text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    <GitBranch className={`size-3.5 shrink-0 ${isActive ? "text-indigo-500" : "text-slate-400"}`} />
                    <span className="flex-1 text-left truncate font-medium">{b.name}</span>
                    {isMain && (
                      <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600 border border-emerald-200/60 shrink-0">
                        live
                      </span>
                    )}
                    {isActive && <Check className="size-3 text-indigo-500 shrink-0" />}
                  </button>
                );
              })
            )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
